import React from 'react';
import { Spinner } from 'react-bootstrap';
import { FaImage, FaVideo } from 'react-icons/fa';
import { API_BASE } from '../config';

// Session-scoped blob URL cache. Keys are the original src URLs.
// Blob URLs are never revoked per-component — they live for the session and are reused
// across all SecureImage instances, preventing refetches on re-mount.
const imageCache = new Map();

export const SecureImage = ({ src, alt, className, style, ...props }) => {
  const cached = src ? imageCache.get(src) : null;
  const [imgData, setImgData] = React.useState({
    url: cached || null,
    loading: !!src && !cached,
    error: null,
  });

  React.useEffect(() => {
    if (!src) { setImgData({ url: null, loading: false, error: null }); return; }

    // Serve from cache instantly — no fetch, no spinner flash.
    if (imageCache.has(src)) {
      setImgData({ url: imageCache.get(src), loading: false, error: null });
      return;
    }

    let isMounted = true;
    const loadImage = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { 'ngrok-skip-browser-warning': '1' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(src, { headers });
        if (!res.ok) throw new Error(`${res.status}`);
        const blob = await res.blob();
        if (isMounted) {
          const blobUrl = URL.createObjectURL(blob);
          imageCache.set(src, blobUrl);
          setImgData({ url: blobUrl, loading: false, error: null });
        }
      } catch (e) {
        if (isMounted) setImgData({ url: null, loading: false, error: e.message });
      }
    };
    loadImage();
    return () => { isMounted = false; };
  }, [src]);

  if (imgData.loading) return <div className={`d-flex align-items-center justify-content-center bg-light ${className}`} style={{ ...style, minHeight: '40px' }}><Spinner animation="border" size="sm" /></div>;
  if (imgData.error)   return <div className={`d-flex flex-column align-items-center justify-content-center bg-danger-subtle text-danger ${className}`} style={{ ...style, minHeight: '40px', fontSize: '0.6rem' }}><FaImage size={14} /><span className="fw-bold">{imgData.error}</span></div>;
  if (!imgData.url)    return <div className={`d-flex align-items-center justify-content-center bg-light ${className}`} style={{ ...style, minHeight: '40px' }}><FaImage size={14} className="text-muted" /></div>;
  return <img src={imgData.url} alt={alt} className={className} style={style} {...props} />;
};

export const SecureVideo = ({ src, className, style, ...props }) => {
  const [videoData, setVideoData] = React.useState({ url: null, loading: true, error: null });
  React.useEffect(() => {
    if (!src) return;
    let isMounted = true;
    const loadVideo = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { 'ngrok-skip-browser-warning': '1' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(src, { headers });
        if (!res.ok) throw new Error(`${res.status}`);
        const blob = await res.blob();
        if (isMounted) {
          const blobUrl = URL.createObjectURL(blob);
          setVideoData({ url: blobUrl, loading: false, error: null });
        }
      } catch (e) {
        if (isMounted) setVideoData({ url: null, loading: false, error: e.message });
      }
    };
    loadVideo();
    return () => { isMounted = false; if (videoData.url) URL.revokeObjectURL(videoData.url); };
  }, [src]);
  if (videoData.loading) return <div className={`d-flex align-items-center justify-content-center bg-light ${className}`} style={{ ...style, minHeight: '100px' }}><Spinner animation="border" size="sm" /></div>;
  if (videoData.error)   return <div className={`d-flex flex-column align-items-center justify-content-center bg-danger-subtle text-danger ${className}`} style={{ ...style, minHeight: '100px', fontSize: '0.6rem' }}><FaVideo size={14} /><span className="fw-bold">{videoData.error}</span></div>;
  return <video src={videoData.url} className={className} style={style} {...props} />;
};

// Defined here (not inside App) so React sees a stable component reference across
// re-renders. Defining it inside App caused unmount+remount on every App state change,
// destroying SecureImage state and triggering a refetch for every gallery image.
export const SmartThumbnail = ({ item }) => {
  const fileUrl = item.thumbnail_file
    ? `${API_BASE}/${item.thumbnail_file}`
    : (item.source_file ? `${API_BASE}/media/${item.source_file.split('/').pop()}` : null);

  if (!item.box_2d || !Array.isArray(item.box_2d) || item.box_2d.length !== 4) {
    return (
      <div className="gallery-thumb-container d-flex align-items-center justify-content-center bg-body-tertiary">
        {fileUrl
          ? <SecureImage src={fileUrl} alt={item.item} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          : <FaImage size={30} className="text-muted" />}
      </div>
    );
  }

  const [ymin, xmin, ymax, xmax] = item.box_2d;
  const boxWidth  = xmax - xmin;
  const boxHeight = ymax - ymin;
  const padding   = 0.4;
  const side      = Math.max(boxWidth, boxHeight) * (1 + padding);
  const centerX   = (xmin + boxWidth  / 2) / 10;
  const centerY   = (ymin + boxHeight / 2) / 10;
  const zoom      = 100 / (side / 10);

  return (
    <div className="gallery-thumb-container">
      <SecureImage
        src={fileUrl}
        alt={item.item}
        className="smart-crop-img"
        style={{ width: `${zoom * 100}%`, height: 'auto', minWidth: '100%', minHeight: '100%', left: '50%', top: '50%', transform: `translate(-${centerX}%, -${centerY}%)` }}
      />
    </div>
  );
};
